/* eslint-disable no-debugger */

import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../../../../../_store/AuthProvider";
import "./MappingUserRoles.css";
import { LoadingScreen } from "mapsted-components";
import { NoClaimsPlaceholder } from "./NoClaimsPlaceholder";
import { useSso } from "./hooks";
import { NotificationContext } from "../../../../../notifications";
import { DeleteClaimModal } from "./DeleteClaimModal";
import { ImportClaimModal } from "./ImportClaimModal";
import { Button, Icon, Input, Popup } from "semantic-ui-react";
import { getLicenceDetailsByName } from "../../../../../../_utils/user";
import { AddClaimModal } from "./AddClaimModal";
import { Checkbox } from "mapsted-components";
import Scrollbars from "react-custom-scrollbars";
import EditIcon from "../../../../../layout/Icons/Edit";
import DeleteIcon from "../../../../../layout/Icons/Delete";
import { useTranslation } from "react-i18next";
import {
    ACCESS_ROLE_PUBLIC,
    SDK_LICENSE_KEY,
    ARRAY_ROLES_TO_FILTER_SSO,
} from "../../../../../../_utils/constants";
import InfoIcon from "../../../../../layout/Icons/Info";
import { useLocalStorageState } from "ahooks";

const MappingUserRoles = ({ addBreadCrumb, removeBreadCrumbAfter }) => {
    // State definitions
    const notification = useContext(NotificationContext);
    const {
        state: { userInfo, properties, licence },
    } = useContext(AuthContext);
    const { t: trans } = useTranslation();

    const companyId = userInfo?.companyId;
    // const userProperties = properties ? Object.values(properties) : [];
    const userProperties = useMemo(() => {
        return properties ? Object.values(properties) : [];
    }, [properties]);

    const assignedProducts = licence?.licenceHash
        ? Object.values(licence.licenceHash).filter(
              ({ name }) => !ARRAY_ROLES_TO_FILTER_SSO.includes(name)
          )
        : [];

    const [isFormChanged, setIsFormChanged] = useState(false);
    const [propertyId, setPropertyId] = useLocalStorageState("property-id", {
        defaultValue: null, // rendered initially if nothing saved yet
    });
    const [claimId, setClaimId] = useLocalStorageState("claim-id", {
        defaultValue: null, // rendered initially if nothing saved yet
    });
    // const [propertyId, setPropertyId] = useState();
    const [propertyName, setPropertyName] = useState("");
    const [propertySearch, setPropertySearch] = useState("");
    const [claimName, setClaimName] = useState(""); // selected claim name
    // const [claimId, setClaimId] = useState(); // selected claim id
    const [isEditClaim, setIsEditClaim] = useState(false); // toggle flag if editing a claim
    const [editAccessId, setEditAccessId] = useState(); // selected claim id
    const [accessRoleId, setAccessRoleId] = useState(); // selected access level
    const [accessRoleName, setAccessRoleName] = useState(); // selected access level
    const [products, setProducts] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);

    const {
        isFetchComplete,
        isClaimsLoading,
        isLevelsLoading,
        isAccessLoading,
        claimList, // list of claims
        accessRolesList, // list of access level e.g. public, staff
        claimAccessData, // list of claims along with saved data (roleId, products etc.)
        // refetchAccessRolesList
        claimsGroupedByPropertyId,
        refetchData,

        // add access
        mutateAddClaimAccess,
        resetAddClaimAccess,
        // delete access
        mutateUpdateClaimAccess,
        resetUpdateClaimAccess,
        // delete access
        mutateSaveClaimProducts,
        resetSaveClaimProducts,
    } = useSso(companyId, propertyId);

    const accessRolePublic = accessRolesList?.find(
        (item) => item.type === ACCESS_ROLE_PUBLIC
    );

    // set breadcrumb
    useEffect(() => {
        const label = trans("Sso.Mapping_Claims");
        addBreadCrumb({
            name: label,
            onClick() {
                removeBreadCrumbAfter(label);
            },
        });
    }, []);

    // handle claim selection
    useEffect(() => {
        if (!isFetchComplete || !claimList || !claimList.length) return;

        const handleClaimSelect = (claimId) => {
            const claim = claimList.find((item) => item.id === claimId);

            // reset the values when a claim is selected
            resetForm();
            setClaimId(claimId);
            setClaimName(claim?.claimName || "");
        };

        handleClaimSelect(claimId ?? claimList[0]?.id);
    }, [isFetchComplete, claimId, claimList?.length]);

    // Set default property
    useEffect(() => {
        if (!isFetchComplete || !userProperties || !userProperties.length)
            return;

        const handlePropertySelect = (propertyId) => {
            const property = userProperties.find(
                (item) => item.propertyId === propertyId
            );

            resetForm();
            setPropertyId(propertyId);
            setPropertyName(property?.name);
        };
        handlePropertySelect(propertyId ?? userProperties[0]?.propertyId);
    }, [isFetchComplete, propertyId, userProperties]);

    // select data based on claim and property selection
    const setClaimData = (claimId, propertyId) => {
        const savedData = claimAccessData?.find(
            (item) => item.ssoClaimId === claimId
        );

        let setDefaultRole = true; // select public role by default
        if (savedData) {
            const products = savedData?.products || [];
            setProducts(products);

            if (propertyId) {
                const accessRole = savedData?.accessRoles.find(
                    (item) => item.propertyId === propertyId
                );
                if (accessRole) {
                    setDefaultRole = false;
                    setAccessRoleId(accessRole.accessRoleId);
                    setEditAccessId(accessRole.id);

                    const accessItem = accessRolesList?.find(
                        (item) => item.userRoleId === accessRole.accessRoleId
                    );
                    // set access role name
                    setAccessRoleName(accessItem?.name?.en || "");
                }
            }
        }

        if (setDefaultRole) {
            setAccessRoleId(accessRolePublic?.userRoleId);
            setAccessRoleName(accessRolePublic?.name?.en);
        }
    };

    useEffect(() => {
        if (!isFetchComplete || (!claimId && !propertyId)) return;

        setClaimData(claimId, propertyId);
    }, [isFetchComplete, claimId, propertyId, claimList?.length]);

    const resetForm = () => {
        setIsFormChanged(false);
        setEditAccessId("");
        setAccessRoleId("");
        setAccessRoleName("");
        setProducts([]);
    };

    const handleEditClaim = (id, name) => {
        setIsEditClaim(true);
        setClaimId(id);
        setClaimName(name);
        handleShowModel();
    };

    const handleAccessRoleSelect = (accessRoleId, name) => {
        if (!claimId) return;

        setIsFormChanged(true);
        setAccessRoleId(accessRoleId);
        setAccessRoleName(name);
    };

    const handleProductSelect = (name) => {
        if (!claimId) return;

        setIsFormChanged(true);
        if (name === "Select All") {
            const isSelectAllChecked = !(
                assignedProducts.length === products.length
            );
            const allProducts = assignedProducts?.map((prod) => prod.name);
            setProducts(isSelectAllChecked ? allProducts : []);
        } else {
            let alreadyChecked = products.includes(name);
            const isChecked = !alreadyChecked;

            if (isChecked) {
                setProducts([...products, name]);
            } else {
                setProducts((prev) =>
                    prev.filter((prodName) => prodName !== name)
                );
            }
        }
    };

    const handleDeleteClaim = () => {
        // setClaimToDelete(claimId);
        setShowDeleteConfirm(true);
    };

    const handleDeleteClaimSuccess = () => {
        refetchData();

        const claims = claimList.filter((item) => item.id !== claimId);
        if (claims?.length) {
            setClaimId(claims[claims.length - 1]?.id);
        } else {
            setClaimId("");
            setClaimName("");
            resetForm();
        }
    };

    // If company admin has the sdk licence
    // add it for the claim as well
    // const AddIfAdminHasSdk = (products) => {
    //     const hasSdkLicence = licence?.licenceHash
    //         ? Object.values(licence.licenceHash).find(
    //               (item) => item.name === SDK_LICENSE_KEY
    //           )
    //         : false;

    //     if (hasSdkLicence && !products.includes(SDK_LICENSE_KEY)) {
    //         return [...products, SDK_LICENSE_KEY];
    //     }

    //     return products;
    // };

    const handleSaveClaimAccess = async () => {
        try {
            if (!claimId || !propertyId || !accessRoleId) {
                console.log("Missing required fields.", {
                    claimId,
                    propertyId,
                    accessRoleId,
                });
                return;
            }

            // const revisedProducts = AddIfAdminHasSdk(products);
            const data = {
                ssoClaimId: claimId,
                propertyId: propertyId,
                accessRoleId: accessRoleId,
                products,
            };

            if (editAccessId) {
                await mutateUpdateClaimAccess({ editAccessId, data });
                resetUpdateClaimAccess();
            } else {
                await mutateAddClaimAccess(data);
                resetAddClaimAccess();
            }

            setIsFormChanged(false);
        } catch (err) {
            console.log("err ", err);
            notification.addNotification(
                "danger",
                trans("Sso.Save_Request_Failed"),
                err.message || trans("Sso.Try_Again_Later")
            );
        } finally {
            setShowModal(false);
        }
    };

    const handleSaveClaimProducts = async () => {
        try {
            if (!claimId || !products?.length) {
                console.log("Missing required fields.", {
                    claimId,
                    products,
                });
                return;
            }

            // const revisedProducts = AddIfAdminHasSdk(products);
            const data = {
                ssoClaimId: claimId,
                products,
            };

            await mutateSaveClaimProducts(data);
            resetSaveClaimProducts();
            setIsFormChanged(false);
        } catch (err) {
            console.log("err ", err);
            notification.addNotification(
                "danger",
                trans("Sso.Save_Request_Failed"),
                err.message || trans("Sso.Try_Again_Later")
            );
        } finally {
            setShowModal(false);
        }
    };

    const handleSubmit = () => {
        if (accessRoleId) {
            handleSaveClaimAccess();
        } else if (products && products.length) {
            handleSaveClaimProducts();
        }
    };

    const handleShowModel = () => {
        setShowModal(true);
    };

    const handleHideModel = () => {
        setIsEditClaim(false);
        setShowModal(false);
    };

    const renderPropertyName = () =>
        propertyId
            ? `${trans("Sso.Property")} (${propertyId}): ${propertyName}`
            : trans("Sso.Select_Property");

    const renderLicenceName = (name) => {
        const licence = getLicenceDetailsByName(name);
        return licence?.title || name;
    };

    const renderPropertyOptions = () => {
        const filteredProperties = propertySearch
            ? userProperties?.filter(
                  ({ propertyId, name }) =>
                      propertyId.toString() === propertySearch ||
                      name?.toLowerCase().includes(propertySearch.toLowerCase())
              )
            : userProperties;

        return filteredProperties?.map(({ propertyId, name }) => (
            <div
                key={propertyId}
                className={`property-dropdown-item ${
                    propertyId.propertyId === propertyId ? "selected" : ""
                }`}
                onClick={() => {
                    setPropertyName(name);
                    setPropertyId(propertyId);
                    setShowPropertyDropdown(false);
                }}
            >
                <span className="property-name">{name}</span>
                <span className="property-id">{propertyId}</span>
            </div>
        ));
    };


    const renderClaims = () =>
        claimList?.map(({ id, claimName }) => (
            <div
                key={id}
                className={`sso-claim-item ${id === claimId ? "selected" : ""}`}
                onClick={() => setClaimId(id)}
            >
                <span className="claim-name">{claimName}</span>
                <div className="claim-actions">
                    <button
                        className="action-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditClaim(id, claimName);
                        }}
                    >
                        <EditIcon />
                    </button>
                    <button
                        className="action-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClaim(id);
                        }}
                    >
                        <DeleteIcon />
                    </button>
                </div>
            </div>
        ));

    const renderAccessLevels = () => {
        // const selectedItem = accessRoleId
        //     ? accessRolesList?.find((item) => item.userRoleId === accessRoleId)
        //     : {};

        const filteredList = accessRolesList?.filter(
            (item) => item.type !== ACCESS_ROLE_PUBLIC
        ); // && item.userRoleId !== accessRoleId

        const revisedList = [
            ...(accessRolePublic ? [accessRolePublic] : []),
            // ...(selectedItem ? [selectedItem] : []),
            ...(filteredList?.length ? filteredList : []),
        ];
        return (
            <div className="access-list-container">
                {revisedList?.map(
                    ({ name: { en: roleName }, userRoleId }, index) => (
                        <div
                            key={`${index}_${userRoleId}`}
                            className={`access-level ${
                                accessRoleId === userRoleId ? "selected" : ""
                            }`}
                            onClick={() =>
                                handleAccessRoleSelect(userRoleId, roleName)
                            }
                        >
                            {roleName}
                        </div>
                    )
                )}
            </div>
        );
    };

    const renderProducts = () => (
        <div className="products-list-container">
            {[{ id: "selectAll", name: "Select All" }, ...assignedProducts].map(
                ({ name }) => (
                    <div key={name} className="product-item">
                        <Checkbox
                            checked={
                                products.length === assignedProducts.length ||
                                products.includes(name)
                            }
                            onChange={() => handleProductSelect(name)}
                            name={name}
                            label={renderLicenceName(name)}
                            className="secondary product-checkbox"
                        />
                    </div>
                )
            )}
        </div>
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showPropertyDropdown &&
                !event.target.closest(".property-dropdown-container")
            ) {
                setShowPropertyDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showPropertyDropdown]);

    const CompAddClaimAccess = (
        <div className="main-content">
            {/* Left Panel - SSO Claims */}
            <div className="left-panel">
                <div className="left-panel-header">
                    <div className="panel-title-section">
                        <h3 className="panel-title">
                            {trans("Sso.ImportClaimModal.Sso_Claim")}
                        </h3>
                        {/* <p className="panel-subtitle">
                            Lorem ipsum amet consectetur.
                            </p> */}
                    </div>
                    <button
                        className="add-new-btn"
                        onClick={() => handleShowModel()}
                    >
                        {trans("Sso.Add_New")}
                    </button>
                </div>
                <div>
                    <Scrollbars
                        autohide="true"
                        autoHeight
                        autoHeightMin={"calc(1vh)"}
                        autoHeightMax={"calc(524px - 86px)"}
                    >
                        <div className="alignerScroll alignerHeight sso-claims-list sso-claims-list-container">
                            {renderClaims()}
                        </div>
                    </Scrollbars>
                </div>
            </div>

            {/* Right Panel - Access & Products */}
            <div className="right-panel">
                <div className="right-panel-content">
                    <div className="management-header">
                        {trans("Sso.Manage_Mapsted_Access_Level")}
                    </div>
                    <div className="access-products">
                        {/* Mapsted Access */}
                        <div className="access-section">
                            <h3 className="section-header">
                                {trans("Sso.Mapsted_Access")}
                            </h3>
                            <div className="property-header">
                                <div className="property-label">
                                    {trans("Sso.Choose_Property")}
                                </div>
                                <div className="property-dropdown-container">
                                    <div
                                        className="property-info"
                                        onClick={() => {
                                            setShowPropertyDropdown(
                                                !showPropertyDropdown
                                            );
                                        }}
                                    >
                                        {renderPropertyName()}
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            className={`dropdown-arrow ${
                                                showPropertyDropdown
                                                    ? "open"
                                                    : ""
                                            }`}
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                fill="#727AFF"
                                            />
                                            <path
                                                d="M8 10l4 4 4-4"
                                                stroke="white"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                    {showPropertyDropdown && (
                                        <div className="property-dropdown">
                                            <div className="property-dropdown-header">
                                                <div>
                                                    <Input
                                                        // disabled={isLoading}
                                                        id="propertySearch"
                                                        light="true"
                                                        className="inputForm searchDashboard"
                                                        icon="search"
                                                        placeholder={trans(
                                                            "Sso.Search"
                                                        )}
                                                        value={propertySearch}
                                                        onChange={(e) => {
                                                            setPropertySearch(
                                                                e.target.value
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            {renderPropertyOptions()}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Scrollbars
                                autohide="true"
                                autoHeight
                                autoHeightMin={"calc(1vh)"}
                                autoHeightMax={"400px"}
                            >
                                <div className="alignerScroll alignerHeight access-level-container">
                                    {isLevelsLoading && (
                                        <div className="ui small active text loader">
                                            {trans("Sso.Loading")}
                                        </div>
                                    )}

                                    {!isLevelsLoading && renderAccessLevels()}
                                </div>
                            </Scrollbars>
                        </div>

                        {/* Products Access */}
                        <div className="products-section">
                            <h3 className="section-header">
                                {trans("Sso.Products_Access_To", {
                                    roleName: accessRoleName,
                                })}{" "}
                                <Popup
                                    content={trans("Sso.Products_Tooltip")}
                                    wide
                                    trigger={
                                        <InfoIcon
                                            width="21"
                                            height="18"
                                            style={{
                                                position: "relative",
                                                top: "4px",
                                            }}
                                        />
                                    }
                                />
                            </h3>
                            <Scrollbars
                                autohide="true"
                                autoHeight
                                autoHeightMin={"calc(1vh)"}
                                autoHeightMax={"350px"}
                            >
                                <div className="alignerScroll alignerHeight products-container">
                                    {renderProducts()}
                                </div>
                            </Scrollbars>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const isLoading = isClaimsLoading || isAccessLoading;
    const disableSaveButton = !isFormChanged;
    return (
        <div className="mapping-user-roles">
            {isLoading && <LoadingScreen />}
            <div className="page-description">
                {trans("Sso.Mapping_Description")}
            </div>
            <div className="property-container">
                <div className="property-header-section">
                    {/* <div className="property-claim-actions">
                        <button
                            className="import-btn"
                            onClick={() => setShowImport(true)}
                        >
                            <ImportIcon />
                            Import
                        </button>
                    </div> */}
                </div>
                {!claimList?.length ? (
                    <NoClaimsPlaceholder
                        isLoading={isClaimsLoading}
                        handleAddNewClick={() => setShowModal(true)}
                    />
                ) : (
                    <div>
                        {CompAddClaimAccess}

                        {/* Save Button */}
                        <div className="save-button-container">
                            <Button
                                color="orange"
                                type="button"
                                floated="right"
                                content={trans("Sso.Save")}
                                onClick={handleSubmit}
                                disabled={disableSaveButton}
                            />
                        </div>
                    </div>
                )}
            </div>

            {showModal && (
                <AddClaimModal
                    companyId={companyId}
                    claimList={claimList}
                    claimId={isEditClaim ? claimId : ""}
                    claimName={isEditClaim ? claimName : ""}
                    handleHideModel={handleHideModel}
                    setClaimId={setClaimId}
                    onSaveClaimCallback={(claimName) => setClaimName(claimName)}
                    refetchData={refetchData}
                />
            )}

            {showDeleteConfirm && (
                <DeleteClaimModal
                    claimName={claimName}
                    claimId={claimId}
                    handleHideModel={() => {
                        setShowDeleteConfirm(false);
                    }}
                    refetchData={handleDeleteClaimSuccess}
                />
            )}

            {showImport && (
                <ImportClaimModal
                    claims={claimList}
                    claimAccessData={claimAccessData}
                    claimsGroupedByPropertyId={claimsGroupedByPropertyId}
                    importToProperyId={propertyId}
                    properties={userProperties}
                    refetchData={refetchData}
                    callbackFn={() => setShowImport(false)}
                />
            )}
        </div>
    );
};

export default MappingUserRoles;
